import { useEffect, useState } from 'react';
import { Badge, Button, Group, Loader, Text } from '@mantine/core';
import { DataTable } from 'mantine-datatable';
import { notifications } from '@mantine/notifications';
import HttpClient from '../helpers/HttpClient';

interface MatchCandidate {
  personId: number;
  personName: string;
  existingPlayerIds: number[];
  playerId: number;
  playerName: string;
  playerPosition: string;
  status: 'new' | 'additional';
}

interface UnmatchedPerson {
  personId: number;
  personName: string;
}

interface MatchSuggestions {
  candidates: MatchCandidate[];
  unmatchedPersons: UnmatchedPerson[];
}

function PlayerIdMatching() {
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [candidates, setCandidates] = useState<MatchCandidate[]>([]);
  const [unmatchedPersons, setUnmatchedPersons] = useState<UnmatchedPerson[]>([]);
  const [selected, setSelected] = useState<MatchCandidate[]>([]);

  const load = () => {
    setLoading(true);
    new HttpClient<MatchSuggestions>('/player/match-suggestions').getSingle()
      .then((result) => {
        const data = result ?? { candidates: [], unmatchedPersons: [] };
        setCandidates(data.candidates);
        setUnmatchedPersons(data.unmatchedPersons);
        setSelected(data.candidates.filter((c) => c.status === 'new'));
      })
      .catch(() => {
        notifications.show({ message: 'Vorschläge konnten nicht geladen werden', color: 'red' });
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const apply = async () => {
    if (selected.length === 0) return;
    setApplying(true);
    try {
      const assignments = selected.map((c) => ({ personId: c.personId, playerId: c.playerId }));
      await new HttpClient('/player').postAction('apply-matches', { assignments });
      notifications.show({ message: `${assignments.length} Spieler-ID(s) übernommen`, color: 'green' });
      load();
    } catch {
      notifications.show({ message: 'Übernehmen fehlgeschlagen', color: 'red' });
    } finally {
      setApplying(false);
    }
  };

  if (loading) return <Loader size="md" />;

  return (
    <>
      <Group mb="md" justify="space-between">
        <Text fw={700} size="lg">Spieler-IDs zuordnen</Text>
        <Group>
          <Button variant="light" onClick={load}>Neu laden</Button>
          <Button loading={applying} disabled={selected.length === 0} onClick={apply}>
            Ausgewählte übernehmen ({selected.length})
          </Button>
        </Group>
      </Group>

      <DataTable
        withTableBorder
        borderRadius="sm"
        withColumnBorders
        striped
        highlightOnHover
        idAccessor="personId"
        records={candidates}
        selectedRecords={selected}
        onSelectedRecordsChange={setSelected}
        columns={[
          { accessor: 'personName', title: 'Person' },
          {
            accessor: 'playerName',
            title: 'Spieler (ÖFB)',
            render: ({ playerName, playerPosition }) =>
              playerPosition ? `${playerName} (${playerPosition})` : playerName,
          },
          {
            accessor: 'status',
            title: 'Status',
            render: ({ status, existingPlayerIds }) =>
              status === 'new'
                ? <Badge color="teal" variant="light">Neu</Badge>
                : <Badge color="orange" variant="light">Zusätzlich zu #{existingPlayerIds.join(', #')}</Badge>,
          },
        ]}
      />

      {candidates.length === 0 && <Text mt="md">Keine Vorschläge gefunden.</Text>}

      {unmatchedPersons.length > 0 && (
        <>
          <Text fw={700} size="md" mt="xl" mb="xs">
            Kein Treffer gefunden ({unmatchedPersons.length})
          </Text>
          <Text size="sm" c="dimmed" mb="xs">
            Für diese Personen wurde kein Spieler mit übereinstimmendem Namen gefunden. Zuordnung ggf. manuell im Personenprofil vornehmen.
          </Text>
          <Text size="sm">
            {unmatchedPersons.map((p) => p.personName).join(', ')}
          </Text>
        </>
      )}
    </>
  );
}

export default PlayerIdMatching;
